/* =========================================================================
   DesktopAuth — /desktop-auth

   The browser half of Codenec Desktop's sign-in (RFC 8252 loopback + PKCE).
   The desktop app opens the SYSTEM browser here with four query parameters,
   the person signs in (inline, if they are not already) and clicks Connect,
   and this page sends them to a one-shot http://127.0.0.1:<port>/callback the
   app is listening on, carrying a single-use code.

   WHY THE SIGN-IN FORM IS INLINE
   The query string IS the flow. Send someone to /login-page and it is gone,
   and the desktop app sits waiting until its 180-second timeout with nothing
   to tell them why.

   WHY THERE IS A CONNECT BUTTON, EVEN FOR SOMEONE ALREADY SIGNED IN
   It is what stops a link someone was SENT from silently pairing a stranger's
   computer to this person's account. Never auto-redirect from this page.

   `state` is a round trip between the desktop app and this page only. It
   never goes to the backend; echo it back exactly as it arrived.

   The desktop app's side of this contract is src/main/pairing.ts in the
   CodenecClientLocalApplication repo.
   ========================================================================= */

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Row, Col } from "reactstrap";
import "../../styles/brand-auth.css";
import useReveal from "hooks/useReveal";
import { useUserAuth } from "../../context/UserContext";
import { getFreshIdToken } from "../../utils/authToken";
import {
  loginUserSessionService,
  requestDesktopPairStart,
} from "../../services/userService";

// `state` and `challenge` are both 32 random bytes, base64url-encoded, minted
// by the desktop app. The backend validates `challenge` with the same pattern.
const BASE64URL_43 = /^[A-Za-z0-9_-]{43}$/;

// Matches MAX_LABEL in backend/src/services/desktopService.js, which rejects
// anything longer. Better to refuse the link here than fail at Connect.
const MAX_LABEL = 120;

const readParams = (searchParams) => {
  const state = searchParams.get("state") || "";
  const challenge = searchParams.get("challenge") || "";
  const portRaw = searchParams.get("port") || "";
  const label = (searchParams.get("label") || "").trim();

  // Digits only, BEFORE Number(): Number("0x1F90") and Number(" 8080 ") are
  // both valid numbers, and neither is a port the desktop app would send.
  const port = /^\d{4,5}$/.test(portRaw) ? Number(portRaw) : NaN;

  const valid =
    BASE64URL_43.test(state) &&
    BASE64URL_43.test(challenge) &&
    Number.isInteger(port) &&
    port >= 1024 &&
    port <= 65535 &&
    label.length <= MAX_LABEL;

  return { valid, state, challenge, port, label };
};

// The IP literal, never "localhost" — the desktop app binds 127.0.0.1 on
// purpose (RFC 8252 s7.3), because on Windows "localhost" often resolves to
// the IPv6 ::1 first and would miss the listener.
const loopbackUrl = (port, params) => {
  const url = new URL(`http://127.0.0.1:${port}/callback`);
  Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  return url.toString();
};

// Firebase Auth error codes → copy keys. The key is kept in state rather than
// the translated string, so a language switch re-renders the message too.
const signInErrorKey = (error) => {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "desktopAuth.errorCredentials";
    case "auth/invalid-email":
      return "desktopAuth.errorInvalidEmail";
    case "auth/too-many-requests":
      return "desktopAuth.errorTooManyAttempts";
    case "auth/network-request-failed":
      return "desktopAuth.errorNetwork";
    default:
      return "desktopAuth.errorSignInFailed";
  }
};

// POST /codenec/desktop/pair/start answers 401 for a token it could not
// verify, 429 past ten codes an hour, and 400 for a challenge or label it
// will not accept. Anything else is on our side.
const connectErrorKey = (error) => {
  const status = error?.response?.status;
  if (status === 401) return "desktopAuth.errorExpired";
  if (status === 429) return "desktopAuth.errorTooMany";
  if (status === 400) return "desktopAuth.errorLinkInvalid";
  return "desktopAuth.errorFailed";
};

const DesktopAuth = () => {
  // The card's entrance only — see styles/premium.css. Purely visual; it
  // reads and changes nothing in the pairing flow.
  useReveal();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user, logIn, logOut } = useUserAuth();

  // Fixed for the life of this pairing attempt.
  const params = useMemo(() => readParams(searchParams), [searchParams]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | connecting | redirecting
  const [errorKey, setErrorKey] = useState("");

  // An anonymous Firebase user holds a perfectly valid ID token, but there is
  // no Codenec account behind it to pair — so it gets the sign-in form.
  const signedIn = !!user && !user.isAnonymous && user.role !== "anonymous";
  const device = params.label || t("desktopAuth.deviceFallback");

  const handleSignIn = async (event) => {
    event.preventDefault();
    setErrorKey("");
    setSigningIn(true);
    try {
      await logIn(email, password);
      setPassword("");
      // Same as the login page, so this browser ends up signed in to
      // codenec.ai the normal way. Pairing does not need the session, and
      // loginUserSessionService swallows its own errors, so this cannot
      // stand in the way of Connect.
      await loginUserSessionService(await getFreshIdToken());
    } catch (error) {
      setErrorKey(signInErrorKey(error));
    } finally {
      setSigningIn(false);
    }
  };

  const handleConnect = async () => {
    setErrorKey("");
    setPhase("connecting");
    try {
      // getFreshIdToken, never user.accessToken: that snapshot is taken at
      // sign-in and never refreshed, so on a tab left open for an hour it
      // fails verifyIdToken and the backend answers 401.
      const token = await getFreshIdToken();
      const { code } = await requestDesktopPairStart(token, params.challenge, params.label);
      if (!code) throw new Error("pair/start answered without a code");

      setPhase("redirecting");
      window.location.href = loopbackUrl(params.port, { code, state: params.state });
    } catch (error) {
      setErrorKey(connectErrorKey(error));
      setPhase("idle");
    }
  };

  // Tell the app straight away, rather than leaving it to its timeout.
  const handleCancel = () => {
    window.location.href = loopbackUrl(params.port, {
      error: "access_denied",
      state: params.state,
    });
  };

  const handleSwitchAccount = async () => {
    setErrorKey("");
    await logOut();
  };

  const busy = phase !== "idle";

  const body = (() => {
    // Nothing is sent back to the app here: with a malformed port or state
    // there is no loopback address it would be safe to redirect to.
    if (!params.valid) {
      return (
        <>
          <h1 className="cn-title">{t("desktopAuth.invalidTitle")}</h1>
          <p className="cn-sub">{t("desktopAuth.invalidBody")}</p>
        </>
      );
    }

    if (phase === "redirecting") {
      return (
        <>
          <h1 className="cn-title">{t("desktopAuth.redirectingTitle")}</h1>
          <p className="cn-sub">{t("desktopAuth.redirectingBody")}</p>
        </>
      );
    }

    if (!signedIn) {
      return (
        <>
          <h1 className="cn-title">{t("desktopAuth.signInTitle")}</h1>
          <p className="cn-sub">{t("desktopAuth.signInBody")}</p>

          <form onSubmit={handleSignIn} style={{ marginTop: 22 }}>
            <div className="cn-field">
              <label className="cn-label" htmlFor="desktop-auth-email">
                {t("login.email")}
              </label>
              <div className="cn-input-wrap">
                <i className="ni ni-email-83 cn-input-icon" aria-hidden="true" />
                <input
                  id="desktop-auth-email"
                  className="cn-input"
                  type="email"
                  autoComplete="email"
                  placeholder={t("login.email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="cn-field cn-field-last">
              <label className="cn-label" htmlFor="desktop-auth-password">
                {t("login.password")}
              </label>
              <div className="cn-input-wrap">
                <i className="ni ni-lock-circle-open cn-input-icon" aria-hidden="true" />
                <input
                  id="desktop-auth-password"
                  className="cn-input"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t("login.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {errorKey && (
              <div className="cn-error" role="alert">
                {t(errorKey)}
              </div>
            )}

            <button
              className="cn-btn-primary cn-btn-block"
              type="submit"
              disabled={signingIn}
            >
              {signingIn ? t("desktopAuth.signingIn") : t("desktopAuth.signInSubmit")}
            </button>
          </form>
        </>
      );
    }

    return (
      <>
        <h1 className="cn-title">{t("desktopAuth.confirmTitle")}</h1>
        <p className="cn-sub">{t("desktopAuth.confirmBody", { device })}</p>
        <p className="cn-hint">{t("desktopAuth.signedInAs", { email: user.email || "" })}</p>

        <div className="cn-success" style={{ marginTop: 18 }}>
          {t("desktopAuth.confirmWarning")}
        </div>

        {errorKey && (
          <div className="cn-error" role="alert">
            {t(errorKey)}
          </div>
        )}

        <button
          className="cn-btn-primary cn-btn-block"
          type="button"
          onClick={handleConnect}
          disabled={busy}
        >
          {phase === "connecting" ? t("desktopAuth.connecting") : t("desktopAuth.connect")}
        </button>
      </>
    );
  })();

  // Cancel exists wherever the app is still waiting on this tab: before
  // sign-in, at the consent step, and after a failed Connect.
  const showCancel = params.valid && phase === "idle";

  return (
    <main className="cn-auth">
      {/* Full height, unlike the login page. brand-auth.css sizes the band
          to calc(100vh - 70px) to leave room for DemoNavbar, and to 0 on
          phones where the footer follows. This page has neither - nothing
          here should offer a way to wander off while the desktop app waits -
          so without this the band stops short and leaves a white strip.
          No top border either: that hairline is where the band meets the
          navbar, and there is no navbar here. */}
      <section className="cn-auth-hero cn-grid" style={{ minHeight: "100vh", borderTop: 0 }}>
        <span className="cn-grain" aria-hidden="true" />
        <span className="cn-orb cn-orb-1" />
        <span className="cn-orb cn-orb-2" />
        <span className="cn-orb cn-orb-3" />
        <Container>
          <Row className="justify-content-center">
            <Col xs="12" md="10" lg="6" xl="5">
              <div className="cn-card" data-reveal>
                <div className="cn-card-head">
                  <span className="cn-eyebrow">
                    <span className="cn-dot" />
                    {t("desktopAuth.eyebrow")}
                  </span>
                </div>
                {body}
              </div>

              {showCancel && (
                <div className="cn-links cn-links-center" data-reveal style={{ "--reveal-delay": "90ms" }}>
                  {signedIn && (
                    <button className="cn-link" type="button" onClick={handleSwitchAccount}>
                      {t("desktopAuth.switchAccount")}
                    </button>
                  )}
                  <button className="cn-link" type="button" onClick={handleCancel}>
                    {t("desktopAuth.cancel")}
                  </button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default DesktopAuth;
